import { ModalComponent } from '../../components/modal/modal'
import { type Nullable } from '../../types/common'
import { PopupStorage } from './popup-storage'
import { PopupType } from './types'
import { POPUP_ANIMATION_DELAY, gradientOpacity } from './utils'
import { ConditionRuntime } from './condition-runtime'
import { POPUP_CLASSNAMES } from './constants'
import { isMobile } from '../../utils/check-media'
import { $el, whenDefined } from 'src/scripts/utils/utils'
import { BodyElement } from 'src/scripts/components/body-element/body-element'

const BODY_ELEMENT_SELECTOR = 'body-element'

export class PopupAgeVerifier extends ModalComponent {
  storage: PopupStorage
  conditionRuntime?: ConditionRuntime
  hasImage: boolean
  hideImgOnMobile: boolean

  constructor() {
    super()
    this.storage = new PopupStorage(this.dataset.blockId ?? this.id)
    this.hasImage = this.dataset.hasImage === 'true'
    this.hideImgOnMobile = this.dataset.hideImgMobile === 'true'
  }

  mountComponent(): void {
    const data = this.storage.loadData()

    if (this.isEditor) {
      this.editor.on('BLOCK_SELECT', this._handleBlockSelect)
      this.editor.on('BLOCK_DESELECT', this._handleBlockDeselect)
      return
    }

    if (!data || (data.type === PopupType.AgeVerifier && !data.verified)) {
      this.conditionRuntime = new ConditionRuntime(
        this.dataset,
        null,
        this.show,
      )
      this.conditionRuntime.run()
    }
  }

  unmountComponent(): void {
    this.conditionRuntime?.cleanup()
  }

  _handleBlockSelect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.show()
    }
  }

  _handleBlockDeselect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.hide()
    }
  }

  show = async (): Promise<void> => {
    const withGradient = !!this.dataset.overlayGradient
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    const background = withGradient
      ? gradientOpacity(
          this.dataset.overlayGradient,
          this.dataset.overlayOpacity,
        )
      : `color-mix(in srgb, ${this.dataset.overlaySolid} ${
          Number(this.dataset.overlayOpacity) * 100
        }%, transparent)`

    const blurFilter = `blur(${this.dataset.overlayBlur}px)`

    whenDefined('body-element').then(() => {
      bodyElement.showOverlay(this.id, {
        zIndex: 8888,
        background,
        backdropFilter: blurFilter,
        '-webkit-backdrop-filter': blurFilter,
        transitionDuration: '0s',
      })
    })

    await super.show()

    this.classList.add(POPUP_CLASSNAMES.lightboxActive)

    this.addListener(this._verifyBtn, 'click', this.onVerify)

    if (this.hasImage) {
      if (this.hideImgOnMobile && isMobile()) {
        return
      }

      setTimeout(() => {
        this.classList.add(POPUP_CLASSNAMES.showImage)
      }, POPUP_ANIMATION_DELAY)
    }
  }

  hide = async (): Promise<void> => {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    whenDefined('body-element').then(() => {
      bodyElement.hideOverlay(this.id)
    })

    this.conditionRuntime?.cleanup()

    this.classList.remove(POPUP_CLASSNAMES.lightboxActive)
    this.classList.add(POPUP_CLASSNAMES.lightboxClosing)

    await super.hide()
    this.classList.remove(POPUP_CLASSNAMES.showImage)
    this.classList.remove(POPUP_CLASSNAMES.lightboxClosing)
  }

  onVerify = (): void => {
    if (!this.isEditor) {
      this.storage.saveData({ type: PopupType.AgeVerifier, verified: true })
    }
    this.removeListener(this._verifyBtn, 'click', this.onVerify)
    this.hide()
  }

  get _verifyBtn(): Nullable<HTMLElement> {
    if (!this.isOpen) {
      return null
    }

    return $el('[data-verify-btn]', this)
  }
}
